import React from 'react';
import { isNumber } from 'lodash';

import { CompactPrice } from '@blockworks/ui';
import { cn } from '@blockworks/ui/utils';

import { PriceChangePlain } from '@/components/common';
import SortChevron from '@/components/pages/grants/components/sort-chevron';
import useSortableData from '@/utils/hooks/use-sortable-data';

export type ETFEntity = {
    id: string;
    ticker: string;
    issuer: string;
    etfName: string;
    custodian: string;
    type: string;
    feeWaiver: number;
    fee: number;
    totalAssets: string;
    avgDailyVol: string;
    volume: string;
    status: string;
    nextDeadline: string;
    finalDeadline: string;
    price?: number;
    marketCap?: number;
    change: number | null;
    tickerUrl?: string;
};

type Props = {
    etfs: ETFEntity[];
    sponsoredEtfs: ETFEntity[];
};
const EtfPageTable = (props: Props) => {
    const { etfs, sponsoredEtfs } = props;
    const {
        items: sortedAndFiltered,
        requestSort,
        sortConfig,
    } = useSortableData<ETFEntity>(etfs, { key: 'totalAssets', direction: 'desc' }, sponsoredEtfs, 'ticker');

    const showNextDeadline = sortedAndFiltered.some(etf => etf.nextDeadline);
    const showFinalDeadline = sortedAndFiltered.some(etf => etf.finalDeadline);

    return (
        <div className="flex flex-col gap-4">
            <table className="min-w-full divide-y divide-gray-200 text-sm scroll-auto">
                <thead>
                    <tr className="text-sm text-left uppercase text-[#6a6a6a]">
                        <td
                            className="px-1 pb-4 w-1/12 hidden xs:table-cell cursor-pointer select-none"
                            onClick={() => requestSort('ticker')}
                        >
                            <div className="flex gap-1 items-center">
                                <span>Ticker</span>
                                {sortConfig?.key === 'ticker' && <SortChevron direction={sortConfig.direction} />}
                            </div>
                        </td>
                        <td
                            className="px-1 pb-4 w-1/12 cursor-pointer select-none"
                            onClick={() => requestSort('issuer')}
                        >
                            <div className="flex gap-1 items-center">
                                <span>Issuer</span>
                                {sortConfig?.key === 'issuer' && <SortChevron direction={sortConfig.direction} />}
                            </div>
                            <p className="xs:hidden">Ticker</p>
                        </td>
                        <td
                            className="px-1 pb-4 w-2/12 hidden lg:table-cell cursor-pointer select-none whitespace-nowrap"
                            onClick={() => requestSort('etfName')}
                        >
                            <div className="flex gap-1 items-center">
                                <span>ETF Name</span>
                                {sortConfig?.key === 'etfName' && <SortChevron direction={sortConfig.direction} />}
                            </div>
                        </td>
                        <td className="px-1 pb-4 w-1/12 cursor-pointer select-none" onClick={() => requestSort('type')}>
                            <div className="flex gap-1 items-center">
                                <span>Type</span>
                                {sortConfig?.key === 'type' && <SortChevron direction={sortConfig.direction} />}
                            </div>
                            <p className="xs:hidden">Status</p>
                        </td>
                        <td
                            className="px-1 pb-4 w-1/12 cursor-pointer select-none"
                            onClick={() => requestSort('price')}
                        >
                            <div className="flex gap-1 items-center">
                                <span>Price</span>
                                {sortConfig?.key === 'price' && <SortChevron direction={sortConfig.direction} />}
                            </div>
                            <p className="xs:hidden">M.Cap</p>
                        </td>
                        <td
                            className="px-1 pb-4 w-1/12 hidden md:table-cell cursor-pointer select-none"
                            onClick={() => requestSort('marketCap')}
                        >
                            <div className="flex gap-1 items-center">
                                <span>Marketcap</span>
                                {sortConfig?.key === 'marketCap' && <SortChevron direction={sortConfig.direction} />}
                            </div>
                        </td>
                        <td
                            className="px-1 pb-4 w-1/12 hidden xl:table-cell cursor-pointer select-none whitespace-nowrap"
                            onClick={() => requestSort('feeWaiver')}
                        >
                            <div className="flex gap-1 items-center">
                                <span>
                                    <span className="hidden xs:inline-block">Fee Waiver</span>
                                </span>
                                {sortConfig?.key === 'feeWaiver' && <SortChevron direction={sortConfig.direction} />}
                            </div>
                            <p className="xs:hidden">Fee Waiver</p>
                        </td>
                        <td
                            className="px-1 pb-4 w-1/12 hidden md:table-cell cursor-pointer select-none whitespace-nowrap"
                            onClick={() => requestSort('fee')}
                        >
                            <div className="flex gap-1 items-center">
                                <span>
                                    <span title="Fee" className="hidden xs:inline-block">
                                        Fee
                                    </span>
                                </span>
                                {sortConfig?.key === 'fee' && <SortChevron direction={sortConfig.direction} />}
                            </div>
                            <p className="xs:hidden">Fee</p>
                        </td>
                        <td
                            className="px-1 pb-4 w-1/12 cursor-pointer select-none whitespace-nowrap"
                            onClick={() => requestSort('totalAssets')}
                        >
                            <div className="flex gap-1 items-center">
                                <span>
                                    <span>AUM</span>
                                </span>
                                {sortConfig?.key === 'totalAssets' && <SortChevron direction={sortConfig.direction} />}
                            </div>
                            <p className="xs:hidden">Volume</p>
                        </td>
                        <td
                            className="px-1 pb-4 w-1/12 hidden xs:table-cell cursor-pointer select-none whitespace-nowrap"
                            onClick={() => requestSort('volume')}
                        >
                            <div className="flex gap-1 items-center">
                                <span>24h Volume</span>
                                {sortConfig?.key === 'volume' && <SortChevron direction={sortConfig.direction} />}
                            </div>
                        </td>
                        <td className="px-1 pb-4 w-2/12 hidden xl:table-cell select-none whitespace-nowrap">
                            <div className="flex gap-1 items-center">
                                <span>Custodian</span>
                            </div>
                        </td>
                        {showNextDeadline && (
                            <td
                                className="px-1 pb-4 w-1/12 hidden cursor-pointer [@media_(min-width:1460px)]:table-cell select-none "
                                onClick={() => requestSort('nextDeadline')}
                            >
                                <div className="flex gap-1 items-center">
                                    <span>Next Deadline</span>
                                    {sortConfig?.key === 'nextDeadline' && (
                                        <SortChevron direction={sortConfig.direction} />
                                    )}
                                </div>
                            </td>
                        )}
                        {showFinalDeadline && (
                            <td
                                className="px-1 pb-4 w-2/12 hidden [@media_(min-width:1460px)]:table-cell cursor-pointer select-none"
                                onClick={() => requestSort('finalDeadline')}
                            >
                                <div className="flex gap-1 items-center">
                                    <span>Final Deadline</span>
                                    {sortConfig?.key === 'finalDeadline' && (
                                        <SortChevron direction={sortConfig.direction} />
                                    )}
                                </div>
                            </td>
                        )}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {sortedAndFiltered.length === 0 && (
                        <tr>
                            <td colSpan={5} className="text-light-gray p-4 text-sm"></td>
                        </tr>
                    )}

                    {sortedAndFiltered.map(etf => {
                        const isSponsoredEtf = sponsoredEtfs.some(sponsoredEtf => sponsoredEtf.ticker === etf.ticker);

                        return (
                            <tr
                                key={etf.id}
                                className={cn(
                                    'group cursor-default',
                                    isSponsoredEtf ? 'bg-purple-50 hover:bg-purple-100' : 'hover:bg-gray-50',
                                )}
                            >
                                <td className="px-0 md:px-2 py-3 hidden xs:table-cell font-bold">
                                    <div className="flex gap-2 justify-start items-center">
                                        <span
                                            className={`w-3 h-3 rounded-full ${etf.status === 'Running' ? 'bg-green-400' : 'bg-blue-400'}`}
                                            title={etf.status}
                                        />
                                        {etf.ticker}
                                    </div>
                                </td>
                                <td className="px-1 py-3 text-black">
                                    {etf.tickerUrl ? (
                                        <a
                                            href={etf.tickerUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 hover:text-brand"
                                        >
                                            {`${etf.issuer}${isSponsoredEtf ? '*' : ''}`}
                                            <p className="xs:hidden font-bold">{etf.ticker}</p>
                                        </a>
                                    ) : (
                                        <>
                                            {`${etf.issuer}${isSponsoredEtf ? '*' : ''}`}
                                            <p className="xs:hidden font-bold">{etf.ticker}</p>
                                        </>
                                    )}
                                </td>
                                <td className="hidden lg:table-cell px-2 py-3 text-gray-500 text-xs">{etf.etfName}</td>
                                <td className="px-2 py-3 text-gray-500">
                                    {etf.type}

                                    <p
                                        className={cn(
                                            'xs:hidden',
                                            etf.status === 'Running' ? 'text-green-400' : 'text-blue-400',
                                        )}
                                    >
                                        {etf.status}
                                    </p>
                                </td>
                                <td className="px-2 py-3">
                                    {(etf.price ?? 0) > 0 ? (
                                        <p className="flex gap-0.5">
                                            <CompactPrice className="text-brand" price={Number(etf.price)} />
                                            {(etf.price ?? 0) > 0 && isNumber(etf.change) && (
                                                <PriceChangePlain
                                                    priceChangePercent={etf.change ?? 0}
                                                    className={cn(
                                                        'text-xxs gap-0 space-x-0',
                                                        etf.change < 0 ? 'text-red-solid' : 'text-green-600',
                                                    )}
                                                />
                                            )}
                                        </p>
                                    ) : (
                                        '-'
                                    )}

                                    <p className="xs:hidden font-medium">
                                        {(etf.marketCap || 0) > 0 && (
                                            <CompactPrice className="text-gray-500" price={Number(etf.marketCap)} />
                                        )}
                                    </p>
                                </td>
                                <td className="hidden md:table-cell px-2 py-3 text-brand">
                                    {(etf.marketCap || 0) > 0 ? <CompactPrice price={Number(etf.marketCap)} /> : '-'}
                                </td>
                                <td className="hidden xl:table-cell px-2 py-3 text-brand">
                                    {etf.feeWaiver ? `${(etf.feeWaiver * 100).toFixed(2)}%` : '-'}
                                </td>
                                <td className="hidden md:table-cell px-2 py-3 text-brand">
                                    {etf.fee ? `${(etf.fee * 100).toFixed(2)}%` : '-'}
                                </td>
                                <td className="px-2 py-3 font-bold">
                                    {etf.totalAssets ? <CompactPrice price={Number(etf.totalAssets)} /> : '-'}
                                    <p className="xs:hidden font-medium">
                                        <CompactPrice price={Number(etf.volume)} />
                                    </p>
                                </td>
                                <td className="hidden xs:table-cell px-2 py-3">
                                    {etf.volume ? (
                                        <CompactPrice price={Number(Number(etf.volume ?? 0) * (etf.price ?? 0))} />
                                    ) : (
                                        '-'
                                    )}
                                </td>
                                <td className="hidden xl:table-cell px-1 py-3 text-black">{etf.custodian}</td>
                                {showNextDeadline && (
                                    <td className="hidden [@media_(min-width:1460px)]:table-cell px-2 py-3 text-gray-600 whitespace-nowrap">
                                        {etf.nextDeadline || '-'}
                                    </td>
                                )}
                                {showFinalDeadline && (
                                    <td className="hidden [@media_(min-width:1460px)]:table-cell px-2 py-3 text-gray-600 whitespace-nowrap">
                                        {etf.finalDeadline || '-'}
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default EtfPageTable;
